import { useCallback, useRef, useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faFilter, faThumbtack } from "@fortawesome/pro-regular-svg-icons";
import { faFilter as faFilterSolid } from "@fortawesome/pro-solid-svg-icons";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { AnimatePresence } from "framer-motion";
import { DateRange, SelectRangeEventHandler } from "react-day-picker";
import { useOnClickOutside } from "usehooks-ts";

import { Icon } from "src/base-components/Icon";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { DropdownItem } from "src/design-system/Dropdown";
import { EventsFilterDropdown } from "src/eventsCatalogue/EventsFilterDropdown";
import { EventFilterFormData } from "src/eventsCatalogue/filters/types";
import { UrlFilterParams } from "src/eventsCatalogue/hooks/useEventFiltersUrl";
import { EVENT_FALLBACK_ICON } from "src/eventsCatalogue/utils";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";

export type FilterMode = "all" | "pinned" | "advanced_filters";

type RelatedEventsFilterDropdownProps = {
  displayName: string;
  hasPinnedEvents: boolean;
  eventType: string;
  schemaIcon?: IconProp;
  filterMode: FilterMode;
  onFilterModeChange: (mode: FilterMode) => void;
  onCustomFiltersOpenChange: (isOpen: boolean) => void;
  onApplyFilters: (
    mode: FilterMode,
    formData?: EventFilterFormData,
    dateRange?: DateRange,
  ) => void;
  onClearFilters: () => void;
  dateRangeValue: DateRange | undefined;
  onDateRangeChange: SelectRangeEventHandler;
  hasFilterCondition?: boolean;
  urlFilterParams?: UrlFilterParams | null;
};

export const RelatedEventsFilterDropdown: React.FC<
  RelatedEventsFilterDropdownProps
> = ({
  displayName,
  hasPinnedEvents,
  eventType,
  schemaIcon,
  filterMode,
  onFilterModeChange,
  onApplyFilters,
  onClearFilters,
  dateRangeValue,
  onDateRangeChange,
  hasFilterCondition,
  onCustomFiltersOpenChange,
  urlFilterParams,
}) => {
  const { featuresEventsEnabled } = useWorkspaceFeatureGates();
  const [openMode, _setOpenMode] = useState<
    "main" | "advanced_filters" | false
  >(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOnClickOutside([containerRef, dropdownRef], () => {
    if (openMode === "main") {
      _setOpenMode(false);
    }
  });

  const isFilterActive =
    filterMode === "pinned" || hasFilterCondition || openMode !== false;

  const setOpenMode = useCallback(
    (
      newOpenMode: "main" | "advanced_filters" | false,
      newFilterMode?: FilterMode,
    ) => {
      if (newFilterMode) {
        onFilterModeChange(newFilterMode);
      }

      _setOpenMode(newOpenMode);
      onCustomFiltersOpenChange?.(
        (filterMode === "advanced_filters" && newOpenMode !== false) ||
          newOpenMode === "advanced_filters",
      );
    },
    [onCustomFiltersOpenChange, filterMode, onFilterModeChange],
  );

  type FilterOption =
    | { key: FilterMode; value: string; icon: IconProp; disabled: boolean }
    | { key: "divider"; isDivider: true };

  const allIcon = schemaIcon ?? EVENT_FALLBACK_ICON;

  const filterOptions: FilterOption[] = [
    { key: "all", value: `All ${displayName}`, icon: allIcon, disabled: false },
    {
      key: "pinned",
      value: `Pinned ${displayName}`,
      icon: faThumbtack,
      disabled: !hasPinnedEvents,
    },
    { key: "divider" as const, isDivider: true as const },
    {
      key: "advanced_filters" as const,
      value: "Apply advanced filters",
      icon: faFilter,
      disabled: false,
    },
  ];

  const handleOpenChange = () => {
    if (typeof openMode === "string") {
      return setOpenMode(false);
    }

    if (filterMode === "advanced_filters") {
      return setOpenMode("advanced_filters");
    }

    return setOpenMode("main");
  };

  const handleFilterModeChange = useCallback(
    (mode: FilterMode) => {
      if (mode === "advanced_filters") {
        // Don't set filteringMode yet - only open the custom filters dropdown.
        // filteringMode will be set to "advanced_filters" when filters are applied.
        setOpenMode("advanced_filters");
      } else {
        onApplyFilters(mode);
        setOpenMode(false, mode);
      }
    },
    [setOpenMode, onApplyFilters],
  );

  const handleApplyFilters = useCallback(
    (formData: EventFilterFormData, dateRange: DateRange | undefined) => {
      onFilterModeChange("advanced_filters");
      onApplyFilters("advanced_filters", formData, dateRange);
    },
    [onApplyFilters, onFilterModeChange],
  );

  return (
    <>
      <Listbox
        ref={containerRef}
        as="div"
        className="relative"
        value={filterMode}
        onChange={handleFilterModeChange}
      >
        <ListboxButton
          className="flex items-center gap-x-1 rounded-lg border-none px-2 py-1.5 focus:outline-none focus:ring-0"
          data-loc="events-filter-dropdown-button"
          onClick={handleOpenChange}
        >
          <Icon
            color={isFilterActive ? "text-indigo-500" : "text-gray-500"}
            icon={isFilterActive ? faFilterSolid : faFilter}
            size="xs"
          />
        </ListboxButton>
        <AnimatePresence>
          {openMode === "main" && (
            <ListboxOptions
              ref={dropdownRef}
              anchor={{ to: "bottom end", gap: 8 }}
              as={MotionDropdownPanel}
              data-loc="events-filter-dropdown"
              static
            >
              <div className="flex h-12 items-center px-4 py-2.5">
                <span className="font-inter-semibold-13px text-gray-800">
                  Show {displayName}
                </span>
              </div>
              {filterOptions.map((option) => {
                if ("isDivider" in option) {
                  return (
                    <hr
                      key={option.key}
                      className="my-2 h-px border-none bg-gray-200"
                    />
                  );
                }
                return (
                  <ListboxOption
                    key={option.key}
                    as={DropdownItem}
                    disabled={option.disabled}
                    value={option.key}
                  >
                    <div className="flex items-center gap-x-2">
                      <Icon
                        color={
                          option.disabled ? "text-gray-400" : "text-gray-500"
                        }
                        icon={option.icon}
                        size="xs"
                      />
                      <span>{option.value}</span>
                    </div>
                  </ListboxOption>
                );
              })}
            </ListboxOptions>
          )}
        </AnimatePresence>
      </Listbox>
      {featuresEventsEnabled && (
        <EventsFilterDropdown
          // We do not need button as we already rendered one above
          buttonRenderer={() => <div className="h-px w-full -translate-y-1" />}
          dateRangeValue={dateRangeValue}
          eventType={eventType}
          hasFilterCondition={hasFilterCondition}
          isOpen={openMode === "advanced_filters"}
          urlFilterParams={urlFilterParams}
          onApplyFilters={handleApplyFilters}
          onClearFilters={onClearFilters}
          onClickBack={() => setOpenMode("main")}
          onDateRangeChange={onDateRangeChange}
          onOpenChange={(isOpen) =>
            setOpenMode(isOpen ? "advanced_filters" : false)
          }
        />
      )}
    </>
  );
};
